import React, { useCallback, ChangeEvent } from 'react'
import { FlexContainer } from '@components/common'
import {
  Dropdown,
  Checkbox,
  Input,
  CurrencyInput,
} from '@components/common/form'

import { AccountCustomServiceFee, FeeTemplate } from '@types'

const StaticFeeTypes = {
  CC_PROCESSING: 'Credit Card Processing Fee',
  REUSABLE_PACKAGING: 'Reusable Packaging Fee',
}

const StaticFees: FeeTemplate[] = [
  {
    type: StaticFeeTypes.CC_PROCESSING,
    loadId: 'cc_processing',
  },
  {
    type: StaticFeeTypes.REUSABLE_PACKAGING,
    loadId: 'reusable_packaging',
  },
]

interface CustomServiceFeesSectionProps {
  accountId: string
  accountCustomServiceFees: AccountCustomServiceFee[]
  onChangeCustomServiceFee: (
    idx: number,
  ) => (
    field: keyof AccountCustomServiceFee,
  ) => (value: string | number | boolean | null) => void
  onAddCustomServiceFee: (serviceFee: AccountCustomServiceFee) => void
  onRemoveCustomServiceFee: (idx: number) => void
  loadServiceFeeTemplate: (
    template: string,
  ) => Promise<AccountCustomServiceFee | null>
}

const CustomServiceFeesSection: React.FC<CustomServiceFeesSectionProps> = ({
  accountId,
  accountCustomServiceFees,
  onChangeCustomServiceFee,
  onAddCustomServiceFee,
  onRemoveCustomServiceFee,
  loadServiceFeeTemplate,
}) => {
  const loadCustomServiceFeeTemplate = useCallback(
    async (template: string) => {
      const customServiceFee = await loadServiceFeeTemplate(template)
      if (!customServiceFee) {
        return
      }
      customServiceFee.accountId = accountId

      onAddCustomServiceFee(customServiceFee)
    },
    [accountId, onAddCustomServiceFee, loadServiceFeeTemplate],
  )

  const onChangeFeeActive = useCallback(
    (type: string) => (active: boolean) => {
      if (active) {
        loadCustomServiceFeeTemplate(type)
      }
    },
    [loadCustomServiceFeeTemplate],
  )

  const renderCustomServiceFees = useCallback(() => {
    return StaticFees.map((staticFee) => {
      const customServiceFeeIdx = accountCustomServiceFees.findIndex(
        (fee) => fee.feeDescription === staticFee.type,
      )

      return (
        <CustomServiceFee
          key={staticFee.type}
          label={staticFee.type}
          index={customServiceFeeIdx}
          customServiceFee={
            customServiceFeeIdx !== -1
              ? accountCustomServiceFees[customServiceFeeIdx]
              : undefined
          }
          onChangeCustomServiceFee={onChangeCustomServiceFee}
          onChangeFeeActive={onChangeFeeActive(staticFee.loadId)}
          onRemoveCustomServiceFee={onRemoveCustomServiceFee}
        />
      )
    })
  }, [
    accountCustomServiceFees,
    onChangeCustomServiceFee,
    onChangeFeeActive,
    onRemoveCustomServiceFee,
  ])

  return (
    <>
      <FlexContainer flexDirection="column" width="100%">
        {renderCustomServiceFees()}
      </FlexContainer>
    </>
  )
}

interface CustomServiceFeeProps {
  label: string
  index: number
  customServiceFee?: AccountCustomServiceFee
  onChangeCustomServiceFee: (
    idx: number,
  ) => (
    field: keyof AccountCustomServiceFee,
  ) => (value: string | number | boolean | null) => void
  onChangeFeeActive: (active: boolean) => void
  onRemoveCustomServiceFee: (idx: number) => void
}
const CustomServiceFee: React.FC<CustomServiceFeeProps> = ({
  label,
  index,
  customServiceFee,
  onChangeCustomServiceFee,
  onChangeFeeActive,
  onRemoveCustomServiceFee,
}) => {
  const active = !!customServiceFee

  const renderValueInput = () => {
    if (!customServiceFee) {
      return null
    }

    if (customServiceFee.valueCalculationType === 'Flat Price') {
      return (
        <CurrencyInput
          label="Value"
          width="100%"
          value={customServiceFee.value}
          prefix="$"
          delay={5}
          onChange={(value: number) =>
            onChangeCustomServiceFee(index)('value')(value)
          }
        />
      )
    }

    return (
      <CurrencyInput
        label="Percent Value"
        width="100%"
        value={customServiceFee.percentValue}
        prefix=""
        suffix="%"
        delay={5}
        onChange={(value: number) =>
          onChangeCustomServiceFee(index)('percentValue')(value)
        }
      />
    )
  }

  const backgroundColor = () => {
    if (customServiceFee?._destroy) {
      return 'bg-red-300'
    }

    if (active) {
      return 'bg-gray-200'
    }

    return 'bg-white'
  }

  return (
    <div
      className={`flex flex-col w-full p-3 rounded-md mb-5 ${backgroundColor()}`}
    >
      {!customServiceFee && (
        <Checkbox
          label={label}
          checked={active}
          marginBottom={active ? '5px' : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onChangeFeeActive(e.target.checked)
          }}
        />
      )}
      {active && customServiceFee && (
        <>
          <div className="flex flex-row justify-between w-full items-center">
            <Input
              label="Fee Description"
              width="30%"
              disabled={true}
              value={customServiceFee.feeDescription}
              onChange={(e) =>
                onChangeCustomServiceFee(index)('feeDescription')(
                  e.target.value,
                )
              }
            />
            <Dropdown
              label="Service Fee Type"
              width="30%"
              disabled={true}
              value={customServiceFee.serviceFeeType}
              onChange={(e) =>
                onChangeCustomServiceFee(index)('serviceFeeType')(
                  e.target.value,
                )
              }
            >
              <option value="Service">Service</option>
              <option value="Delivery">Delivery</option>
              <option value="Processing">Processing</option>
            </Dropdown>
            <Dropdown
              label="Value Calculation Type"
              width="35%"
              disabled={true}
              value={customServiceFee.valueCalculationType}
              onChange={(e) => {
                onChangeCustomServiceFee(index)('valueCalculationType')(
                  e.target.value,
                )
              }}
            >
              <option value="Flat Price">Flat Price</option>
              <option value="Percent of Subtotal">Percent of Subtotal</option>
            </Dropdown>
          </div>
          <div className="flex flex-row justify-between w-full items-start">
            <div style={{ width: '30%' }}>{renderValueInput()}</div>
            <div style={{ width: '30%', marginTop: '20px' }}>
              <Checkbox
                label="Tax Exempt"
                width="100%"
                disabled={true}
                checked={customServiceFee.isTaxExempt}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChangeCustomServiceFee(index)('isTaxExempt')(
                    e.target.checked,
                  )
                }
              />
            </div>
            <div style={{ width: '35%' }}>
              {!customServiceFee.isTaxExempt && (
                <Input
                  label="Item Tax Code"
                  disabled={true}
                  value={customServiceFee.itemTaxCode || ''}
                  onChange={(e) =>
                    onChangeCustomServiceFee(index)('itemTaxCode')(
                      e.target.value,
                    )
                  }
                />
              )}
            </div>
          </div>
          <div className="flex flex-row justify-between w-full items-center mt-3">
            <button
              className="bg-red-500 text-white px-4 py-2 rounded-md font-bold"
              onClick={() => onRemoveCustomServiceFee(index)}
            >
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default CustomServiceFeesSection
